<script setup lang="ts">
import { motion } from 'motion-v'
import type { IntRange } from 'type-fest'

const props = defineProps<{
  timelineIndex: StringifiedInt<IntRange<0, 6>>
  timelineItem: {
    title: string
    description: string
    items: Record<number, string>
    note: string
  }
}>()

const { tm, rt } = useI18n()
const isScreenLG = useMediaQuery('(min-width: 1024px)')

function getStaticAssets(index: StringifiedInt<IntRange<0, 6>>) {
  const baseUrl = `${S3_ASSETS_URL}/how-it-works`

  return match(index)
    .with('0', () => ({
      background: `${baseUrl}/customers/background.jpg`,
      foreground: `${baseUrl}/customers/foreground.png`,
    }))
    .with('1', () => ({
      background: `${baseUrl}/engagement/background.jpg`,
      foreground: `${baseUrl}/engagement/foreground.png`,
    }))
    .with('2', () => ({
      background: `${baseUrl}/bookings/background.jpg`,
      foreground: `${baseUrl}/bookings/foreground.png`,
    }))
    .with('3', () => ({
      background: `${baseUrl}/discovery/background.jpg`,
      foreground: `${baseUrl}/discovery/foreground.png`,
    }))
    .with('4', () => ({
      background: `${baseUrl}/fullness/background.jpg`,
      foreground: `${baseUrl}/fullness/foreground.png`,
    }))
    .with('5', () => ({
      background: `${baseUrl}/roi/background.jpg`,
      foreground: `${baseUrl}/roi/foreground.png`,
    }))
    .exhaustive()
}
</script>

<template>
  <div
    class="grid grid-cols-[min-content_1fr] place-items-center gap-5 lg:grid-cols-[1fr_min-content_1fr] lg:px-4"
  >
    <motion.div
      :class="['grid w-full gap-y-5 pb-4 pt-10 lg:py-14', {
        'lg:order-last': Number(props.timelineIndex) % 2 !== 0
      }]"
      :initial="isScreenLG ? {
        opacity: 0,
        x: Number(props.timelineIndex) % 2 !== 0 ? 50 : -50
      } : { opacity: 1, x: 0 }"
      :while-in-view="isScreenLG ? {
        opacity: 1,
        x: 0,
        transition: { delay: 0.2, duration: 0.5 }
      } : {}"
      :exit="isScreenLG ? {
        opacity: 0,
        x: -10,
        transition: { delay: 0.2, duration: 0.5 }
      } : {}"
    >
      <h3 class="text-balance text-3xl font-medium">
        {{ rt(timelineItem.title) }}
      </h3>

      <p class="text-neutral-600">
        {{ rt(timelineItem.description) }}
      </p>

      <ul class="grid gap-y-2 font-medium">
        <li
          v-for="(listItem, listIndex) in tm(`how_it_works.timeline.${props.timelineIndex}.items`)"
          :key="listIndex"
          class="flex items-center gap-x-2"
        >
          <span class="rounded-[50%] bg-orange-500/10 p-1 text-sm text-orange-500">
            <UiIcon name="tick-solid" />
          </span>

          {{ rt(listItem) }}
        </li>
      </ul>

      <div
        class="rounded-lg bg-orange-500/10 p-2 text-orange-700/90"
        role="alert"
      >
        <span class="font-bold">Result: </span>
        {{ rt(timelineItem.note) }}
      </div>
    </motion.div>

    <motion.div
      class="relative order-first row-span-2 size-full origin-top before:inline-flex before:size-10 before:items-center before:justify-center before:rounded-[50%] before:bg-orange-500 before:text-xl before:font-semibold before:tabular-nums before:text-white before:ring-4 before:ring-orange-400/30 before:content-[counter(timeline)] before:[counter-increment:timeline] after:absolute after:left-1/2 after:z-[-1] after:h-full after:-translate-x-1/2 after:border-r after:border-dashed after:border-baltic-200 lg:order-none lg:row-span-1"
      :initial="isScreenLG ? { scale: 0.5 } : { scale: 1 }"
      :while-in-view="isScreenLG ? {
        scale: 1,
        transition: { duration: 0.2, delay: 0.1 }
      } : {}"
      :exit="isScreenLG ? { scale: 0.5 } : {}"
    />

    <motion.div
      :class="['relative col-start-2 mb-10 flex size-full max-h-96 justify-center overflow-clip rounded-[1.875rem] bg-cover p-4 lg:col-start-auto lg:mb-0', {
        'lg:order-first': Number(props.timelineIndex) % 2 !== 0,
        'items-center': ['0', '3', '4'].includes(props.timelineIndex),
        'items-end !pb-0': ['1', '2', '3', '5'].includes(props.timelineIndex),
        'bg-bottom': props.timelineIndex === '3'
      }]"
      :style="{
        backgroundImage: `url(${getStaticAssets(props.timelineIndex).background})`
      }"
      :initial="isScreenLG ? { opacity: 0, y: 100 } : { opacity: 1, y: 0 }"
      :while-in-view="isScreenLG ? {
        opacity: 1,
        y: 0,
        transition: {
          delay: 0.2,
          duration: 0.5
        }
      } : {}"
      :exit="isScreenLG ? { opacity: 0, y: 100 } : {}"
    >
      <img
        :src="getStaticAssets(props.timelineIndex).foreground"
        :class="['object-contain', {
          'h-3/4': ['0', '3', '4'].includes(props.timelineIndex),
          'h-5/6': ['1', '2', '3', '5'].includes(props.timelineIndex)
        }]"
      >
    </motion.div>
  </div>
</template>
